/**
 * Retouren v3 (sessie 172): de lijst op aandacht.
 *
 * Maat: ontwerp 90 en de mock (desktop-lijst-en-dossier.html,
 * iphone-lijst.html). Kop met het aantal besluiten, zoekveld met
 * "Nieuwe retour", drie filters, groepen als gewone zinnen, per regel
 * naam, product, stand-zin en bedrag. Geen badges, geen tellers.
 *
 * Filter en zoekterm houdt de pagina vast (op de telefoon verdwijnt de
 * lijst als het dossier open is en moet hij terugkomen zoals hij was).
 * Zoeken werkt over alles: de API negeert het filter zodra er een
 * zoekterm is.
 *
 * Sessie 186: elke lopende retour heeft op zijn rij een snelle actie
 * "Afronden" (afgerond buiten het portaal), met een eigen paneel onder de
 * rij: reden kiezen, dan bevestigen.
 */
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import {
  retourenV3Api,
  type RetourV3Filter,
  type RetourV3Groep,
  type RetourV3Lijst,
  type RetourV3Regel,
} from "@/lib/retouren-v3-api";

import { AfrondenBuitenPortaal } from "./v3/AfrondenBuitenPortaal";
import { BUITEN_PORTAAL_LABEL, kanBuitenPortaal } from "./v3/buitenPortaal";

/** Wachttijd na de laatste toets voordat er gezocht wordt. */
export const ZOEK_VERTRAGING_MS = 300;

export const RETOUREN_V3_LIJST_SLEUTEL = ["retouren-v3", "lijst"] as const;

const FILTERS: { sleutel: RetourV3Filter; label: string }[] = [
  { sleutel: "aandacht", label: "Aandacht" },
  { sleutel: "loopt", label: "Loopt" },
  { sleutel: "alles", label: "Alles" },
];

export function euroV3(cents: number): string {
  const bedrag = (cents / 100).toLocaleString("nl-NL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `€ ${bedrag}`;
}

function tijdAmsterdam(iso: string): string | null {
  const moment = new Date(iso);
  if (Number.isNaN(moment.getTime())) return null;
  return new Intl.DateTimeFormat("nl-NL", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Amsterdam",
  }).format(moment);
}

/** Alle retour-id's in lijstvolgorde, de leden van een bundel op hun plek. */
export function retourIdsVan(lijst: RetourV3Lijst | undefined): string[] {
  if (!lijst) return [];
  const ids: string[] = [];
  for (const groep of lijst.groepen) {
    for (const regel of groep.regels) {
      if (regel.soort === "bundel") {
        for (const lid of regel.leden) if (lid.retour_id) ids.push(lid.retour_id);
      } else if (regel.retour_id) {
        ids.push(regel.retour_id);
      }
    }
  }
  return ids;
}

function besluitTekst(aantal: number, smal: boolean): string | null {
  if (aantal <= 0) return null;
  if (smal) return `${aantal} om je besluit`;
  return aantal === 1 ? "1 vraagt om jouw besluit" : `${aantal} vragen om jouw besluit`;
}

function Klapteken() {
  return (
    <svg
      className="rv3-klap"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 6l4 4 4-4" />
    </svg>
  );
}

/** De snelle actie op een rij: welke rij heeft het paneel open. */
interface Afronden {
  groep: RetourV3Groep;
  openId: string | null;
  onOpen: (retourId: string | null) => void;
  onKlaar: () => void;
}

function Regel(props: {
  regel: RetourV3Regel;
  gemarkeerd: string | null;
  smal: boolean;
  lid?: boolean;
  onKies: (retourId: string) => void;
  afronden: Afronden;
}) {
  const { regel, gemarkeerd, lid, onKies, afronden } = props;
  const id = regel.retour_id;
  const kan =
    id !== null && regel.soort === "retour" && kanBuitenPortaal(afronden.groep, regel.stand_code);
  const knop = (
    <button
      type="button"
      className={lid ? "rv3-regel rv3-regel-lid" : "rv3-regel"}
      aria-current={id !== null && id === gemarkeerd ? "true" : undefined}
      data-sleutel={regel.sleutel}
      onClick={() => {
        if (id) onKies(id);
      }}
    >
      <span className="rv3-regel-tekst">
        <span className="rv3-naam">{regel.naam}</span>
        {regel.product ? (
          <span className="rv3-product">{regel.product}</span>
        ) : null}
        <span className="rv3-zin">{regel.stand_zin}</span>
      </span>
      {regel.bedrag_cents !== null ? (
        <span className="rv3-bedrag">{euroV3(regel.bedrag_cents)}</span>
      ) : null}
    </button>
  );
  if (!kan || id === null) return knop;
  const open = afronden.openId === id;
  return (
    <div className="rv3-regelrij">
      <div className="rv3-regelrij-boven">
        {knop}
        <button
          type="button"
          className="rv3-rijactie"
          data-testid="rv3-rij-afronden"
          aria-expanded={open}
          aria-label={`${BUITEN_PORTAAL_LABEL}: ${regel.naam}`}
          title={BUITEN_PORTAAL_LABEL}
          onClick={() => afronden.onOpen(open ? null : id)}
        >
          Afronden
        </button>
      </div>
      {open ? (
        <div className="rv3-rijpaneel">
          <AfrondenBuitenPortaal
            retourId={id}
            retournummer={regel.retournummer}
            onKlaar={afronden.onKlaar}
            onSluiten={() => afronden.onOpen(null)}
          />
        </div>
      ) : null}
    </div>
  );
}

function Bundel(props: {
  regel: RetourV3Regel;
  gemarkeerd: string | null;
  smal: boolean;
  onKies: (retourId: string) => void;
  afronden: Afronden;
}) {
  const { regel, gemarkeerd, smal, onKies, afronden } = props;
  const bevatGekozen =
    gemarkeerd !== null && regel.leden.some((lid) => lid.retour_id === gemarkeerd);
  // Zolang JP niet zelf klapt: open als de gekozen retour erin staat.
  const [geklapt, setGeklapt] = useState<boolean | null>(null);
  const open = geklapt ?? bevatGekozen;
  const ledenId = `rv3-leden-${regel.sleutel}`;
  return (
    <>
      <button
        type="button"
        className="rv3-regel"
        aria-expanded={open}
        aria-controls={ledenId}
        data-sleutel={regel.sleutel}
        onClick={() => setGeklapt(!open)}
      >
        <span className="rv3-regel-tekst">
          <span className="rv3-naam">{regel.naam}</span>
          {regel.product && !smal ? (
            <span className="rv3-product">{regel.product}</span>
          ) : null}
          <span className="rv3-zin">{regel.stand_zin}</span>
        </span>
        <Klapteken />
      </button>
      {open ? (
        <ul className="rv3-regels" id={ledenId} aria-label={regel.naam}>
          {regel.leden.map((lid) => (
            <li key={lid.sleutel}>
              <Regel
                regel={lid}
                gemarkeerd={gemarkeerd}
                smal={smal}
                lid
                onKies={onKies}
                afronden={afronden}
              />
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
}

export interface RetourenLijstV3Props {
  filter: RetourV3Filter;
  onFilter: (filter: RetourV3Filter) => void;
  zoek: string;
  onZoek: (zoek: string) => void;
  /** De gekozen retour; krijgt de blauwe rand. */
  gekozenId: string | null;
  onKies: (retourId: string) => void;
  /** Na elke geladen lijst: alle retour-id's in volgorde. */
  onLijst?: (ids: string[]) => void;
  onNieuw: () => void;
  smal: boolean;
}

export function RetourenLijstV3(props: RetourenLijstV3Props) {
  const { filter, onFilter, zoek, onZoek, gekozenId, onKies, onLijst, onNieuw, smal } = props;

  // Het veld is direct; de zoekvraag volgt na een korte stilte.
  const [invoer, setInvoer] = useState(zoek);
  const onZoekRef = useRef(onZoek);
  const onLijstRef = useRef(onLijst);
  useLayoutEffect(() => {
    onZoekRef.current = onZoek;
    onLijstRef.current = onLijst;
  });
  useEffect(() => {
    const schoon = invoer.trim();
    if (schoon === zoek) return;
    const wacht = window.setTimeout(() => onZoekRef.current(schoon), ZOEK_VERTRAGING_MS);
    return () => window.clearTimeout(wacht);
  }, [invoer, zoek]);

  const qc = useQueryClient();
  const [afrondenId, setAfrondenId] = useState<string | null>(null);
  const [afgerondMelding, setAfgerondMelding] = useState<string | null>(null);

  const lijst = useQuery({
    queryKey: [...RETOUREN_V3_LIJST_SLEUTEL, filter, zoek],
    queryFn: () => retourenV3Api.lijst({ filter, zoek: zoek || undefined }),
    placeholderData: keepPreviousData,
  });

  const ids = useMemo(() => retourIdsVan(lijst.data), [lijst.data]);
  useEffect(() => {
    if (lijst.data && !lijst.isPlaceholderData) onLijstRef.current?.(ids);
  }, [ids, lijst.data, lijst.isPlaceholderData]);

  // Op de telefoon staat er geen keuze in de url zolang de lijst in beeld
  // is; de mock toont de bovenste dan toch gemarkeerd.
  const gemarkeerd = gekozenId ?? (smal ? (ids[0] ?? null) : null);

  const data = lijst.data;
  const teller = data ? besluitTekst(data.aantal_besluit, smal) : null;
  const picqerTijd = data?.picqer_gecontroleerd_op
    ? tijdAmsterdam(data.picqer_gecontroleerd_op)
    : null;
  const toonOnderregel = data && !zoek && filter !== "alles" && data.aantal_afgerond > 0;

  function afrondenVoor(groep: RetourV3Groep): Afronden {
    return {
      groep,
      openId: afrondenId,
      onOpen: (id) => {
        setAfgerondMelding(null);
        setAfrondenId(id);
      },
      onKlaar: () => {
        setAfrondenId(null);
        setAfgerondMelding(
          "Afgerond buiten het portaal. Er is geen mail en geen geld via het portaal gegaan.",
        );
        // Lijst en dossier (ook van de retour die rechts openstaat) opnieuw.
        void qc.invalidateQueries({ queryKey: ["retouren-v3"] });
      },
    };
  }

  const nieuwKnop = (
    <button type="button" className="rv3-knop" onClick={onNieuw}>
      Nieuwe retour
    </button>
  );

  return (
    <section
      aria-label="Retourlijst"
      className={smal ? "rv3-lijst rv3-lijst-smal" : "rv3-lijst"}
    >
      <div className="rv3-lijstkop vo-statusbalkvrij">
        <div className="rv3-kop">
          <h1>Retouren</h1>
          {teller ? <span className="rv3-kop-teller">{teller}</span> : null}
        </div>
        <div className="rv3-zoekrij">
          <label htmlFor="rv3-zoek" className="rv3-verborgen">
            Zoek op klant, order of retournummer
          </label>
          <input
            id="rv3-zoek"
            className="rv3-zoek"
            type="search"
            placeholder="Klant, order of retournummer"
            value={invoer}
            onChange={(e) => setInvoer(e.target.value)}
          />
          {smal ? null : nieuwKnop}
        </div>
        <div className="rv3-filters" role="group" aria-label="Filter">
          {FILTERS.map((f) => (
            <button
              key={f.sleutel}
              type="button"
              className="rv3-filter"
              aria-pressed={filter === f.sleutel}
              onClick={() => onFilter(f.sleutel)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="rv3-vlak">
        {afgerondMelding ? (
          <p className="rv3-lijstmelding" role="status" data-testid="rv3-afgerond-melding">
            {afgerondMelding}
          </p>
        ) : null}
        {lijst.isLoading ? (
          <p className="rv3-lijstleeg">Bezig met laden…</p>
        ) : lijst.isError || !data ? (
          <p className="rv3-lijstleeg" role="alert">
            De retouren zijn niet op te halen. Ververs de pagina.
          </p>
        ) : data.groepen.length === 0 ? (
          <p className="rv3-lijstleeg">
            {zoek ? `Geen retour gevonden voor “${zoek}”.` : "Hier staat nu niets."}
          </p>
        ) : (
          data.groepen.map((groep) => (
            <div className="rv3-groep" key={groep.sleutel} data-groep={groep.sleutel}>
              <h2 className="rv3-groepkop">{groep.titel}</h2>
              <ul className="rv3-regels" aria-label={groep.titel}>
                {groep.regels.map((regel) => (
                  <li key={regel.sleutel}>
                    {regel.soort === "bundel" ? (
                      <Bundel
                        regel={regel}
                        gemarkeerd={gemarkeerd}
                        smal={smal}
                        onKies={onKies}
                        afronden={afrondenVoor(groep.sleutel)}
                      />
                    ) : (
                      <Regel
                        regel={regel}
                        gemarkeerd={gemarkeerd}
                        smal={smal}
                        onKies={onKies}
                        afronden={afrondenVoor(groep.sleutel)}
                      />
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))
        )}
        {toonOnderregel ? (
          <p className="rv3-onderregel">
            {data.aantal_afgerond === 1
              ? "1 afgeronde retour staat onder Alles."
              : `${data.aantal_afgerond} afgeronde retouren staan onder Alles.`}
            {picqerTijd ? ` Picqer is gecontroleerd om ${picqerTijd}.` : ""}
          </p>
        ) : null}
      </div>

      {smal ? (
        <div className="rv3-voet">
          <button type="button" className="rv3-hoofdknop" onClick={onNieuw}>
            Nieuwe retour
          </button>
        </div>
      ) : null}
    </section>
  );
}
